import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BackupDestination,
  ExportSettings,
  MyAccountContactInfo,
  MyAccountPhone,
  OdataObject,
  StorageAccount,
  StorageState
} from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MyAccountService {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly odataDomains = this.odataFactory.CreateService<any>('Domains');
  private readonly odataSingleUsers = this.odataFactory.CreateService<any>('SingleUsers');
  private readonly odataExportSettings = this.odataFactory.CreateService<ExportSettings>('ExportSettings');

  private readonly errorRoleText = 'Caught error manually: Forbidden role';

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;
  protected readonly isDomainAdmin = this.ability.isDomainAdmin;
  protected readonly isDomainUser = this.ability.isDomainUser;
  protected readonly isSingleUser = this.ability.isSingleUser;

  getContactInfo(): Observable<MyAccountContactInfo> {
    return this.http.get<MyAccountContactInfo>(
      this.odataFactory.CreateService<MyAccountContactInfo>(`/BackupUsers(${this.authUserId()})/GetContactInfo`).Query().GetUrl()
    );
  }

  setContactInfo(payload: MyAccountPhone): Observable<MyAccountPhone> {
    return this.http.post<MyAccountPhone>(
      this.odataFactory.CreateService<MyAccountPhone>(`/BackupUsers(${this.authUserId()})/SetContactInfo`).Query().GetUrl(),
      payload
    );
  }

  deleteAccount(password: string): Observable<any> {
    if (this.isDomainAdmin()) {
      return this.deleteAccountDomainAdmin(this.authDomainId(), password);
    }

    if (this.isSingleUser()) {
      return this.deleteAccountSingleUser(this.authUserId(), password);
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private deleteAccountDomainAdmin(id: string, password: string): Observable<any> {
    return this.odataDomains.CustomAction(id, 'DeleteAccount', { password });
  }

  private deleteAccountSingleUser(id: string, password: string): Observable<any> {
    return this.odataSingleUsers.CustomAction(id, 'DeleteAccount', { password });
  }

  getStorages(): Observable<StorageAccount[]> {
    return this.http.get<OdataObject<StorageAccount[]>>(this.odataFactory.CreateService<StorageAccount>('/Storages').Query().GetUrl()).pipe(
      map((res) => res?.value ?? []),
      catchError(() => of([]))
    );
  }

  getStorage(): Observable<BackupDestination> {
    if (this.isAdmin()) {
      return this.getStorageAdmin(this.authDomainId());
    }

    if (this.isSingleUser()) {
      return this.getStorageSingleUser(this.authUserId());
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private getStorageAdmin(id: string): Observable<BackupDestination> {
    return this.odataDomains.CustomFunction(id, 'GetStorage').pipe(map((res: HttpResponse<BackupDestination>) => res.body));
  }

  private getStorageSingleUser(id: string): Observable<BackupDestination> {
    return this.odataSingleUsers.CustomFunction(id, 'GetStorage').pipe(map((res: HttpResponse<BackupDestination>) => res.body));
  }

  setStorage(payload: BackupDestination): Observable<BackupDestination> {
    if (this.isAdmin()) {
      return this.setStorageAdmin(this.authDomainId(), payload);
    }

    if (this.isSingleUser()) {
      return this.setStorageSingleUser(this.authUserId(), payload);
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private setStorageAdmin(id: string, payload: BackupDestination): Observable<BackupDestination> {
    return this.odataDomains.CustomAction(id, 'SetStorage', payload).pipe(map((res: HttpResponse<BackupDestination>) => res.body));
  }

  private setStorageSingleUser(id: string, payload: BackupDestination): Observable<BackupDestination> {
    return this.odataSingleUsers.CustomAction(id, 'SetStorage', payload).pipe(map((res: HttpResponse<BackupDestination>) => res.body));
  }

  testStorage(payload: BackupDestination): Observable<OdataObject<StorageState>> {
    if (this.isAdmin()) {
      return this.testStorageAdmin(this.authDomainId(), payload);
    }

    if (this.isSingleUser()) {
      return this.testStorageSingleUser(this.authUserId(), payload);
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private testStorageAdmin(id: string, payload: BackupDestination): Observable<OdataObject<StorageState>> {
    return this.odataDomains.CustomAction(id, 'TestStorage', payload).pipe(map((res: HttpResponse<OdataObject<StorageState>>) => res.body));
  }

  private testStorageSingleUser(id: string, payload: BackupDestination): Observable<OdataObject<StorageState>> {
    return this.odataSingleUsers
      .CustomAction(id, 'TestStorage', payload)
      .pipe(map((res: HttpResponse<OdataObject<StorageState>>) => res.body));
  }

  deleteStorage(password: string): Observable<any> {
    if (this.isAdmin()) {
      return this.deleteStorageAdmin(this.authDomainId(), password);
    }

    if (this.isSingleUser()) {
      return this.deleteStorageSingleUser(this.authUserId(), password);
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private deleteStorageAdmin(id: string, password: string): Observable<any> {
    return this.odataDomains.CustomAction(id, 'DeleteStorage', { password });
  }

  private deleteStorageSingleUser(id: string, password: string): Observable<any> {
    return this.odataSingleUsers.CustomAction(id, 'DeleteStorage', { password });
  }

  resetAlternatePassword(payload: { Email: string }): Observable<void> {
    return this.http.post<void>(
      this.odataFactory.CreateService(`/BackupUsers(${this.authUserId()})/ResetAlternatePassword`).Query().GetUrl(),
      payload
    );
  }

  getExportSettings(): Observable<ExportSettings> {
    if (this.isAdmin() || this.isDomainUser()) {
      return this.getExportSettingsCommon(this.authDomainId());
    }

    if (this.isSingleUser()) {
      return this.getExportSettingsCommon(this.authUserId());
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private getExportSettingsCommon(id: string): Observable<ExportSettings> {
    return this.odataExportSettings.Get(id).Exec();
  }

  setExportSettings(payload: ExportSettings): Observable<ExportSettings> {
    if (this.isAdmin()) {
      return this.setExportSettingsCommon(this.authDomainId(), payload);
    }

    if (this.isSingleUser()) {
      return this.setExportSettingsCommon(this.authUserId(), payload);
    }

    return throwError(() => new Error(this.errorRoleText));
  }

  private setExportSettingsCommon(id: string, payload: ExportSettings): Observable<ExportSettings> {
    return this.odataExportSettings.Put<ExportSettings>(payload, id).Exec();
  }
}
